'use client';

import { useTranslations } from 'next-intl';
import { lazy, Suspense, useState } from 'react';
import { Button } from '@//components/Button';
import { InfoStrip } from '@//components/InfoStrip';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';

const AutomotiveAssociationModal = lazy(() => import('./AutomotiveAssociationModal'));

// The market settings JSONs share the SVG path but only raster-only markets
// (currently Drive/NBF) ship the 1x/2x PNG pair. MarketMetadata is inferred
// from wayke.json — which no longer carries the raster keys — so the runtime
// raster fields are declared here as optional.
type AutomotiveAssociationLogotypeAsset = {
  svg: string;
  width: number;
  height: number;
  '1x'?: string;
  '2x'?: string;
};

export const AutomotiveAssociationBanner: React.FC = () => {
  const t = useTranslations('utils.automotiveAssociation');
  const marketMetadata = useMarketMetadata();
  const [modal, setModal] = useState(false);

  const name = marketMetadata.automotiveAssociation.name;
  const logotypeAsset = marketMetadata.automotiveAssociation
    .logotype as AutomotiveAssociationLogotypeAsset;
  // Prefer the SVG when the market provides one — resolution-independent and
  // far smaller than the raster pair. Markets without an SVG (currently
  // Drive/NBF) fall through to their 1x/2x PNG pair. width/height are
  // intrinsic dimensions used by the browser to reserve layout space and
  // avoid CLS; the on-screen size is set by CSS in InfoStrip (.logotype).
  const logotypeBase = {
    alt: name,
    width: logotypeAsset.width,
    height: logotypeAsset.height,
  };
  const rasterSrc = logotypeAsset['1x'];
  const logotype = logotypeAsset.svg
    ? { ...logotypeBase, src: logotypeAsset.svg }
    : rasterSrc
      ? { ...logotypeBase, src: rasterSrc, srcSet: logotypeAsset['2x'] }
      : undefined;

  return (
    <>
      <InfoStrip
        ariaLabel={t('banner.ariaLabel', { name })}
        logotype={logotype}
        action={
          <Button
            onClick={() => setModal(true)}
            title={t('banner.action.title', { name })}
            variant="outline"
            size="sm"
            noWrap
          >
            {t('banner.action.label', { name })}
          </Button>
        }
        testId="seller-automotive-association-banner"
        heading={t('banner.heading', { name })}
        headingLevel={3}
        description={t('banner.description', { name })}
      />
      <Suspense fallback={null}>
        <AutomotiveAssociationModal isOpen={modal} onClose={() => setModal(false)} />
      </Suspense>
    </>
  );
};
